import React, { useState, useEffect } from 'react';
import { performanceMonitor, SystemHealth } from '../services/performanceMonitor';
import { userExperienceTracker, UXMetrics } from '../services/userExperienceTracker';

const PerformanceDashboard: React.FC = () => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [uxMetrics, setUxMetrics] = useState<UXMetrics | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateMetrics = () => {
      setSystemHealth(performanceMonitor.getSystemHealth());
      setUxMetrics(userExperienceTracker.getUXMetrics());
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 30000); // OPTIMIZED: Changed from 5000 to 30000 (30 seconds)

    return () => clearInterval(interval);
  }, []);

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'degrading': return '📉';
      default: return '➡️';
    }
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 z-50"
        title="Performans Dashboard"
      >
        📊
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-80 z-50">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">Sistem Performansı</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      {systemHealth && (
        <div className="space-y-3">
          {/* System Health Score */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Sistem Sağlığı</span>
            <span className={`px-2 py-1 rounded text-sm font-medium ${getHealthColor(systemHealth.score)}`}>
              {systemHealth.score}/100
            </span>
          </div>

          {/* Trends */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="text-center">
              <div className="text-gray-500">Navigation</div>
              <div>{getTrendIcon(systemHealth.trends.navigation)}</div>
            </div>
            <div className="text-center">
              <div className="text-gray-500">Memory</div>
              <div>{getTrendIcon(systemHealth.trends.memory)}</div>
            </div>
            <div className="text-center">
              <div className="text-gray-500">API</div>
              <div>{getTrendIcon(systemHealth.trends.api)}</div>
            </div>
          </div>

          {/* Predictions - OPTIMIZED: Only show high confidence warnings */}
          {systemHealth.predictions.nextIssue > 0 && systemHealth.predictions.confidence >= 0.8 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
              <div className="text-xs text-yellow-800">
                ⚠️ Potansiyel sorun: {Math.round(systemHealth.predictions.nextIssue)} dakika
              </div>
              <div className="text-xs text-yellow-600">
                {systemHealth.predictions.type} (güven: {Math.round(systemHealth.predictions.confidence * 100)}%)
              </div>
            </div>
          )}
        </div>
      )}

      {uxMetrics && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Kullanıcı Deneyimi</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className="text-gray-500">Oturum Süresi</div>
              <div className="font-medium">{Math.round(uxMetrics.sessionDuration / 60)}dk</div>
            </div>
            <div>
              <div className="text-gray-500">Sayfa Görüntüleme</div>
              <div className="font-medium">{uxMetrics.pageViews}</div>
            </div>
            <div>
              <div className="text-gray-500">Hata Oranı</div>
              <div className="font-medium">{(uxMetrics.errorRate * 100).toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-gray-500">Etkileşim Skoru</div>
              <div className="font-medium">{Math.round(uxMetrics.engagementScore)}/100</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-gray-200 flex gap-2">
        <button
          onClick={() => {
            const data = userExperienceTracker.exportData();
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `performance-${Date.now()}.json`;
            a.click();
          }}
          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
        >
          Dışa Aktar
        </button>
        <button
          onClick={() => {
            userExperienceTracker.clearData();
            setUxMetrics(userExperienceTracker.getUXMetrics());
          }}
          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200"
        >
          Temizle
        </button>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
